import { DivConst } from 'constants/divConst';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useFieldArray, useForm } from 'react-hook-form';
import type { Path, UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { VoiceChatResponseType } from 'types/api/voiceChatResponseType';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';
import {
  buildEditFormFromResponse,
  buildInitialVoiceChatForm,
  convertVoiceChatValuesToParam,
} from 'pages/ws/voiceChatEdit/utils/voiceChatFormConverter';
import { submit } from 'utils/app';
import { openPreviewUrl } from 'utils/voiceChat';
import { getProductionUrl } from 'utils/terms';
import { collectRequiredErrors } from 'pages/ws/voiceChatEdit/utils/voiceChatValidation';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

/**
 * useWsVoiceChatEdit の戻り値型。
 */
export interface UseWsVoiceChatEditResult {
  // form
  useFormMethods: UseFormReturn<VoiceChatEditFormStateType>;
  questionFieldArray: UseFieldArrayReturn<VoiceChatEditFormStateType, 'questions', 'id'>;
  respondentFieldArray: UseFieldArrayReturn<VoiceChatEditFormStateType, 'respondentFields', 'id'>;

  // status flags
  initialized: boolean;
  processingOnSave: boolean;
  processingOnPreview: boolean;
  processingOnPublish: boolean;
  processingOnPublishTerminate: boolean;
  processingOnReserveCancel: boolean;
  processingOnDelete: boolean;

  // domain state
  voiceChatState: VoiceChatResponseType | null;
  isExpiredPlan: boolean;
  canPublished: boolean;
  isUnpublished: () => boolean;
  isReadOnly: () => boolean;

  // modal state
  modalPublishOpenState: boolean;
  setModalPublishOpenState: (open: boolean) => void;
  modalConfirmOpenState: boolean;
  setModalConfirmOpenState: (open: boolean) => void;
  modalConfirmTitle: string;

  // handlers
  onSave: () => Promise<void>;
  onPreview: () => Promise<void>;
  onPublishModalOpen: () => Promise<void>;
  onConfirmModalSubmit: () => void;
  onPublishModalSubmit: (attr: {
    publishStartAt: number | null;
    publishEndAt: number | null;
    noIndexFlag: boolean;
  }) => Promise<void>;
  onPublishTerminate: () => Promise<void>;
  onReserveCancel: () => Promise<void>;
  onDelete: () => Promise<void>;
  onCreateVoiceChatRef: React.MutableRefObject<(() => void) | undefined>;

  // shared
  navigate: ReturnType<typeof useNavigate>;
  confirm: ReturnType<typeof useAppState>['confirm'];
}

// ------------------------------------------------------------------ //
// hook
// ------------------------------------------------------------------ //

/**
 * ボイスチャット作成・編集画面のカスタムフック。
 * すべての状態・副作用・APIコール・フォーム初期化を集約する（useWsFormEdit のミラー）。
 */
export function useWsVoiceChatEdit(): UseWsVoiceChatEditResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { voiceChatCd } = useParams<{ voiceChatCd: string }>();

  const [acctState] = useAtom(AcctState);

  const { updateAcctState, noticeMessageSuccess, noticeMessageFailure, confirm, canPublish } =
    useAppState();
  const {
    postVoiceChatRef,
    postVoiceChatPublish,
    postVoiceChatPublishTerminate,
    postVoiceChatDelete,
    postVoiceChatUpdate,
    postVoiceChatRegister,
  } = useApiState();

  // ローディングの制御設定
  const [initialized, setInitialized] = useState(false);
  const [processingOnSave, setProcessingOnSave] = useState(false);
  const [processingOnPreview, setProcessingOnPreview] = useState(false);
  const [processingOnPublish, setProcessingOnPublish] = useState(false);
  const [processingOnPublishTerminate, setProcessingOnPublishTerminate] = useState(false);
  const [processingOnReserveCancel, setProcessingOnReserveCancel] = useState(false);
  const [processingOnDelete, setProcessingOnDelete] = useState(false);

  // ボイスチャット情報
  const [voiceChatState, setVoiceChatState] = useState<VoiceChatResponseType | null>(null);
  const [modalPublishOpenState, setModalPublishOpenState] = useState<boolean>(false);
  const [modalConfirmOpenState, setModalConfirmOpenState] = useState<boolean>(false);
  const [modalConfirmTitle, setModalConfirmTitle] = useState<string>('');

  // フォーム設定
  const useFormMethods = useForm<VoiceChatEditFormStateType>();
  const { control, handleSubmit, setValue, getValues, setError } = useFormMethods;
  const questionFieldArray = useFieldArray({
    control,
    name: 'questions',
  });
  const respondentFieldArray = useFieldArray({
    control,
    name: 'respondentFields',
  });

  // バリデーションメッセージ
  const validAlertMessage = '入力に誤りがあります。赤文字で表示されている箇所を確認してください。';
  // プラン（利用期間切れ）
  const isExpiredPlan = acctState.company.plan.isExpired;
  // 公開可能
  const canPublished = canPublish(DivConst.items.contentsType.voiceChat.value);

  // チャットボットコンポーネントから onCreateVoiceChat 関数を呼び出すための ref
  const onCreateVoiceChatRef = useRef<(() => void) | undefined>(undefined);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        // eslint-disable-next-line react-hooks/immutability -- 初回マウント時にhoisting相当で_initStateを呼ぶ設計
        await _initState();
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時 1 回のみ実行する初期化処理
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 保存ボタン押下時の処理
   */
  const onSave = async () =>
    submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(
        async () => {
          try {
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }

            const response = await _save(
              voiceChatState?.voiceChatStatusDiv ?? DivConst.items.voiceChatStatus.unpublished.value
            );
            setVoiceChatState(response);

            noticeMessageSuccess('ボイスチャットの保存が完了しました。');
          } catch (e) {
            showBoundary(e);
          }
        },
        () => {
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * プレビューボタン押下時の処理
   */
  const onPreview = async () =>
    submit(
      processingOnPreview,
      setProcessingOnPreview,
      handleSubmit(
        async () => {
          try {
            let voiceChat = voiceChatState;

            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }

            if (_isUnpublished()) {
              voiceChat = await _save(DivConst.items.voiceChatStatus.unpublished.value);
              voiceChat = await postVoiceChatPublish({
                voiceChatId: voiceChat.id,
                mode: 'preview',
              });
              setVoiceChatState(voiceChat);

              confirm({
                title: 'ボイスチャットの保存が完了しました',
                message: `このままプレビュー画面を開きますか？`,
                onSubmit: async () => {
                  openPreviewUrl(voiceChat!.previewCd);
                },
              });
            } else {
              openPreviewUrl(voiceChat!.previewCd);
            }
          } catch (e) {
            showBoundary(e);
          }
        },
        () => {
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * 公開ボタン押下時の処理
   */
  const onPublishModalOpen = async () =>
    submit(
      processingOnPublish,
      setProcessingOnPublish,
      handleSubmit(
        async () => {
          try {
            if (!getValues().privacyPolicyUrl) {
              setModalConfirmTitle('プライバシーポリシーが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().termsUrl) {
              setModalConfirmTitle('利用規約が未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().copyright) {
              setModalConfirmTitle('コピーライトが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }
            setModalPublishOpenState(true);
          } catch (e) {
            showBoundary(e);
          }
        },
        () => {
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * 未設定項目確認モーダルのOKボタンがクリックされた時の処理
   */
  const onConfirmModalSubmit = () => {
    setModalConfirmOpenState(false);
    submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async () => {
        try {
          if (!_validQuestionCount()) {
            noticeMessageFailure(validAlertMessage);
            return;
          }

          const response = await _save(
            voiceChatState?.voiceChatStatusDiv ?? DivConst.items.voiceChatStatus.unpublished.value
          );
          setVoiceChatState(response);
          navigate(`/setting/${acctState.company.cd}/company`);
        } catch (e) {
          showBoundary(e);
        }
      })
    );
  };

  /**
   * 公開設定完了時の処理。
   */
  const onPublishModalSubmit = async (attr: {
    publishStartAt: number | null;
    publishEndAt: number | null;
    noIndexFlag: boolean;
  }) => {
    try {
      setProcessingOnPublish(true);
      setValue(
        'publishStartAt',
        attr.publishStartAt as VoiceChatEditFormStateType['publishStartAt']
      );
      setValue('publishEndAt', attr.publishEndAt as VoiceChatEditFormStateType['publishEndAt']);
      setValue('noIndexFlag', attr.noIndexFlag);

      let response = await _save(DivConst.items.voiceChatStatus.reserved.value);

      if (attr.publishStartAt == null) {
        response = await postVoiceChatPublish({
          voiceChatId: response.id,
          mode: 'production',
        });
        updateAcctState(['company', 'activeContent', 'usage'], acctState.company.cd);
      } else {
        response = await postVoiceChatRef({
          voiceChatCd: response.cd,
        });
      }

      setVoiceChatState(response);
      setProcessingOnPublish(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 公開停止ボタン押下時の処理
   */
  const onPublishTerminate = async () => {
    try {
      setProcessingOnPublishTerminate(true);
      setVoiceChatState(
        await postVoiceChatPublishTerminate({
          voiceChatId: voiceChatState!.id,
          mode: 'production',
        })
      );
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      setProcessingOnPublishTerminate(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 予約取消ボタン押下時の処理
   */
  const onReserveCancel = async () => {
    try {
      if (!_validQuestionCount()) {
        noticeMessageFailure(validAlertMessage);
        return;
      }
      setProcessingOnReserveCancel(true);
      const response = await _save(DivConst.items.voiceChatStatus.unpublished.value);
      setVoiceChatState(response);
      setProcessingOnReserveCancel(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 削除ボタン押下時の処理
   */
  const onDelete = async () => {
    try {
      setProcessingOnDelete(true);
      await postVoiceChatDelete({ voiceChatId: voiceChatState!.id });
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      setProcessingOnDelete(false);
      navigate(`/ws/${acctState.company.cd}/content/search`);
    } catch (e) {
      showBoundary(e);
    }
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の状態を初期化します。
   */
  const _initState = async () => {
    // 新規登録の場合
    if (!voiceChatCd) {
      const initialValues = buildInitialVoiceChatForm(acctState);
      setValue('privacyPolicyUrl', acctState.company.privacyPolicyUrl ?? '');
      setValue('copyright', acctState.company.copyright ?? '');
      setValue(
        'termsUrl',
        acctState.company.termsInputDiv === DivConst.items.termsInputType.url.value
          ? (acctState.company.termsUrl ?? '')
          : getProductionUrl(acctState.company.cd)
      );

      (Object.entries(initialValues) as [keyof VoiceChatEditFormStateType, unknown][]).forEach(
        ([key, value]) => {
          setValue(key, value as VoiceChatEditFormStateType[typeof key]);
        }
      );

      onCreateVoiceChatRef.current?.();
    }
    // 更新の場合
    else {
      const response = await postVoiceChatRef({ voiceChatCd });
      setVoiceChatState(response);

      setValue('privacyPolicyUrl', acctState.company.privacyPolicyUrl ?? '');
      setValue('copyright', acctState.company.copyright ?? '');
      setValue(
        'termsUrl',
        acctState.company.termsInputDiv === DivConst.items.termsInputType.url.value
          ? (acctState.company.termsUrl ?? '')
          : getProductionUrl(acctState.company.cd)
      );

      const editValues = buildEditFormFromResponse(response);
      (Object.entries(editValues) as [keyof VoiceChatEditFormStateType, unknown][]).forEach(
        ([key, value]) => {
          setValue(key, value as VoiceChatEditFormStateType[typeof key]);
        }
      );
    }
  };

  /**
   * ボイスチャット情報を保存します。
   */
  const _save = async (voiceChatStatusDiv: number): Promise<VoiceChatResponseType> => {
    // eslint-disable-next-line no-useless-assignment -- 後続の条件分岐で上書きされるため初期値としてnullを設定
    let response: VoiceChatResponseType | null = null;
    const commonParam = {
      ...convertVoiceChatValuesToParam(getValues()),
      popupImageKey: getValues().popupImageKey,
    };
    // 新規登録の場合
    if (!voiceChatState) {
      response = await postVoiceChatRegister({
        companyId: acctState.company.id,
        voiceChatStatusDiv,
        ...commonParam,
      });
    }
    // 更新の場合
    else {
      response = await postVoiceChatUpdate({
        voiceChatId: voiceChatState.id,
        voiceChatStatusDiv,
        respondentFieldGroupId: voiceChatState.respondentFieldGroupId ?? null,
        ...commonParam,
      } as unknown as Parameters<typeof postVoiceChatUpdate>[0]);
    }
    updateAcctState(['company', 'activeContent'], acctState.company.cd);

    return response!;
  };

  /**
   * 公開時に必須となる項目を検証します。
   */
  const _validRequiredControllers = (): boolean => {
    const values = getValues();
    const errors = collectRequiredErrors(values);
    errors.forEach(error => {
      setError(error.name as Path<VoiceChatEditFormStateType>, {
        type: 'custom',
        message: error.message,
      });
    });
    return errors.length === 0;
  };

  /**
   * 設問数の上限のみを検証します。
   * 予約取消・未設定項目確認モーダル OK は未設定項目を残したまま保存できる既存仕様のため、
   * 全項目検証（_validRequiredControllers）ではなくこちらを使います。
   */
  const _validQuestionCount = (): boolean => {
    const errors = collectRequiredErrors(getValues()).filter(error => error.name === 'questions');
    errors.forEach(error => {
      setError(error.name as Path<VoiceChatEditFormStateType>, {
        type: 'custom',
        message: error.message,
      });
    });
    return errors.length === 0;
  };

  /**
   * 未稼働かどうかを判定します。
   */
  const _isUnpublished = (): boolean => {
    return (
      voiceChatState == null ||
      voiceChatState.voiceChatStatusDiv === DivConst.items.voiceChatStatus.unpublished.value
    );
  };

  /* ----------------------------------------------------------------------- */
  /* 公開関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 未稼働かどうかを判定します（公開向け）。
   */
  const isUnpublished = (): boolean => _isUnpublished();

  /**
   * 読み取り専用かどうかを判定します。
   */
  const isReadOnly = (): boolean => {
    return (
      voiceChatState != null &&
      voiceChatState.voiceChatStatusDiv !== DivConst.items.voiceChatStatus.unpublished.value
    );
  };

  /* ----------------------------------------------------------------------- */
  /* 戻り値 */
  /* ----------------------------------------------------------------------- */
  return {
    useFormMethods,
    questionFieldArray,
    respondentFieldArray,
    initialized,
    processingOnSave,
    processingOnPreview,
    processingOnPublish,
    processingOnPublishTerminate,
    processingOnReserveCancel,
    processingOnDelete,
    voiceChatState,
    isExpiredPlan,
    canPublished,
    isUnpublished,
    isReadOnly,
    modalPublishOpenState,
    setModalPublishOpenState,
    modalConfirmOpenState,
    setModalConfirmOpenState,
    modalConfirmTitle,
    onSave,
    onPreview,
    onPublishModalOpen,
    onConfirmModalSubmit,
    onPublishModalSubmit,
    onPublishTerminate,
    onReserveCancel,
    onDelete,
    onCreateVoiceChatRef,
    navigate,
    confirm,
  };
}
